import {AfterViewInit,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  OnInit,
  viewChild,
  ViewContainerRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {distinctUntilChanged, Observable, of, switchMap, tap, zip} from 'rxjs';
import {AUTHENTICATION_SERVICE,
  AuthenticationService,
  AuthenticationState} from './shared/services/authentication/authentication.service';
import {ActivatedRoute, NavigationEnd, NavigationError, Router, RouterOutlet} from '@angular/router';
import {AsyncPipe, JsonPipe, Location, LocationStrategy, NgClass, PathLocationStrategy} from '@angular/common';
import {environment} from '../environments/environment';
import {MatDialog} from '@angular/material/dialog';
import {BreakpointService} from './shared/services/breakpoint/breakpoint.service';
import {filter, map} from 'rxjs/operators';
import {InsightsService} from './shared/services/insights/insights.service';
import {AthenaService} from './shared/services/athena/athena.service';
import {NavBarComponent} from './shared/views/nav-bar/nav-bar.component';
import {SidebarComponent} from './shared/views/sidebar/sidebar.component';
import {Title} from '@angular/platform-browser';
import {AirtelRedirectService} from './prudent-plus/airtel-redirect/airtel-redirect.service';
import {WebVitalsService} from './shared/services/web-vital/web-vital.service';
import {EnvironmentService} from './shared/services/environment/environment.service';
import {FeatureFlagsService} from './shared/services/feature-flag/feature-flags.service';
import {Permission} from './shared/services/permission/permission';
import {HasPermissions, PERMISSION_SERVICE} from './shared/services/permission/permission.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    Location,
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
  ],
  imports: [
    AsyncPipe,
    NavBarComponent,
    NgClass,
    SidebarComponent,
    JsonPipe,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  public static readonly CHUNK_LOAD_RELOAD_KEY = 'chunkLoadReload';
  private static readonly CHUNK_LOAD_ERROR_PATTERNS = [
    /chunkloaderror/i,
    /loading chunk .* failed/i,
    /failed to fetch dynamically imported module/i,
    /importing a module script failed/i,
  ];

  private readonly authenticationService = inject<AuthenticationService>(AUTHENTICATION_SERVICE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly breakpointService = inject(BreakpointService);
  private readonly dialog = inject(MatDialog);
  private readonly insightsService = inject(InsightsService);
  private readonly athenaService = inject(AthenaService);
  private readonly titleService = inject(Title);

  protected readonly airtelRedirectService = inject(AirtelRedirectService);
  protected readonly deviceSize$ = this.breakpointService.deviceSize$;
  protected readonly AuthenticationState = AuthenticationState;
  protected readonly athenaMessage$ = this.athenaService.messages$;
  protected readonly webVitals = inject(WebVitalsService);
  protected readonly environmentService = inject(EnvironmentService);
  protected readonly featureFlagsService = inject(FeatureFlagsService);
  protected readonly permissionService = inject<HasPermissions>(PERMISSION_SERVICE);
  readonly title = this.environmentService.isPrudentPlus ? 'Prudent Plus' : 'Prudent Insurance Brokers';

  get showHelpBarTrigger$(): Observable<boolean> {
    if (this.environmentService.isPrudentPlus) {
      return of(true);
    }

    if (this.environmentService.isPx) {
      return zip([
        this.featureFlagsService.loadFeatureFlags(),
        this.permissionService.hasPermissionsLoaded(Permission.QMS_AI_DATA_QUERY),
      ]).pipe(
        map(([
          _,
          hasPermissions,
        ]) => {
          return hasPermissions && this.featureFlagsService.pxAiDataQuery;
        })
      );
    }

    return of(false);
  }

  protected readonly authenticationState$ = this.authenticationService.loadUserData().pipe(
    switchMap(() => {
      return this.authenticationService.state$.pipe(
        distinctUntilChanged(),
        tap((state) => {
          let path = this.location.path();
          const url = new URL(window.location.origin + path);

          if (url.searchParams.has('source')) {
            localStorage.setItem('source', url.searchParams.get('source') || '');
            url.searchParams.delete('source');
            path = url.pathname + url.search;
          }
          const source = localStorage.getItem('source');

          this.insightsService.logEvent('PP_USER_SOURCE_TRACKING', {
            url: window.location.href,
            source: source,
          });

          setTimeout(() => {
            localStorage.removeItem('source');
          }, 1000);

          localStorage.removeItem('selectedIndex');
          switch (state) {
              case AuthenticationState.UNAUTHENTICATED:
                if (path === '/verify-email' || path === '/reset-password' || path === '/identity-provider') {
                  return;
                }
                this.router.navigate(['login']);
                break;
              case AuthenticationState.AUTHENTICATING:
                break;
              case AuthenticationState.AUTHENTICATED:
                if (
                  path.length === 0 ||
                path === '/login' ||
                path === '/verify-email'
                ) {
                  this.router.navigate(['dashboard']);
                } else {
                  this.router.navigate([path]);
                }
                break;
          }
        })
      );
    })
  );

  protected readonly vcr = viewChild('viewContainerRef', {
    read: ViewContainerRef,
  });

  protected ref?: ComponentRef<any>;

  showHelpBar = false;

  ngOnInit(): void {
    // This metric is sent to Azure only when the user is using the Prudent Plus app
    if (this.environmentService.isPrudentPlus) {
      this.webVitals.initWebVitals();
    }
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        sessionStorage.removeItem(AppComponent.CHUNK_LOAD_RELOAD_KEY);
        const currentRoute = this.router.routerState.root;

        this.setTitle(currentRoute);
      }

      if (event instanceof NavigationError && this.isChunkLoadError(event.error)) {
        // When a new deployment occurs, the cached index.html may reference outdated chunk URLs.
        // Browsers throw well-known chunk/dynamic import errors when cached HTML points to removed chunks.
        // Reloading the page ensures the latest index.html and chunk URLs are loaded.
        if (!sessionStorage.getItem(AppComponent.CHUNK_LOAD_RELOAD_KEY)) {
          sessionStorage.setItem(AppComponent.CHUNK_LOAD_RELOAD_KEY, '1');
          this.reloadPage();
        }
      }
    });

    this.athenaService.openHelpBar$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_open) => {
        this.showHelpBar = _open;
      });

    this.dialog.afterOpened.subscribe((ref) => {
      ref.beforeClosed().subscribe(() => {
        document
          .querySelector('.mat-mdc-dialog-panel')
          ?.classList.add('closing');
      });
    });
    this.insightsService.logPageView('App Component');

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.showHelpBar = false;
    });

    document.body.classList.add(...environment.appConfig.baseThemes);
  }

  ngAfterViewInit() {
    this.authenticationState$
      .pipe(filter((state) => state === AuthenticationState.AUTHENTICATED))
      .subscribe(() => {
        const vcr = this.vcr();

        if (environment.athenaOverrides.helpComponent && vcr) {
          this.ref = vcr.createComponent(environment.athenaOverrides.helpComponent);
          this.ref.location.nativeElement.style.width = '100%';
        }
      });
  }

  setTitle(route: ActivatedRoute): void {
    while (route.firstChild) {
      route = route.firstChild;
    }
    route.data.subscribe((data) => {
      const title = data['title'] || this.environmentService.isPrudentPlus ? 'Prudent Plus' : 'Prudent Insurance Brokers';

      this.titleService.setTitle(title);
    });
  }

  toggleHelpBar() {
    this.showHelpBar = !this.showHelpBar;
  }

  private isChunkLoadError(error: unknown): boolean {
    const isObjectError = error !== null && typeof error === 'object';
    const name = isObjectError && 'name' in error && typeof error.name === 'string' ? error.name : '';
    const message = isObjectError && 'message' in error && typeof error.message === 'string' ? error.message : '';
    const errorText = `${name} ${message}`.toLowerCase();

    return AppComponent.CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(errorText));
  }

  private reloadPage(): void {
    window.location.reload();
  }
}
